import React, { useEffect, useState } from "react";
import { WidgetInputLabel } from "./widgetInputLabel/widgetInputLabel";
import { WidgetFormTextInput } from "./widgetFormTextInput/widgetFormTextInput";
import { intl } from "../../i18n/TypedIntl";
import { emailIsGood, logger } from "../../utils";
import { PhoneHandler, PhoneHandlerGoogle } from "../../services/PhoneHandlerGoogle";

const WAIT_TIME = 700;

interface Props {
  onChange: (contact: string) => void;
  phoneRegion?: string;
}

export const ContactInput = ({ onChange, phoneRegion = "" }: Props) => {
  const [contact, setContact] = useState<string>("");
  const [hasError, setHasError] = useState(false);
  const [timer, setTimer] = useState<number>();
  const [phoneHandler, setPhoneHandler] = useState<PhoneHandler>();

  useEffect(() => {
    PhoneHandlerGoogle.getInstance()
      .then(setPhoneHandler)
      .catch((e) => {
        logger.error({ message: "Couldn't set phone handler", error: e });
      });

    return () => window.clearTimeout(timer);
  }, []);

  const isPhoneValid = (input: string): boolean => {
    if (!input) {
      return true;
    }

    return !!phoneHandler && phoneHandler.isValid(input);
  };

  const handleBlur = () => {
    if (timer !== undefined) {
      clearTimeout(timer);
    }

    validateAndSignalChange(contact);
  };

  const handleChange = (value: string) => {
    if (timer !== undefined) {
      clearTimeout(timer);
    }

    setTimer(window.setTimeout(() => validateAndSignalChange(value), WAIT_TIME));
    setHasError(false);
    setContact(value);
  };

  const validateAndSignalChange = (value: string) =>
    new Promise<void>((resolve, reject) => {
      if (emailIsGood(value) || isPhoneValid(normalizeContact(value))) {
        onChange(normalizeContact(value));
        setHasError(false);
        resolve();
      } else {
        setHasError(true);
        reject();
      }
    });

  return (
    <>
      <WidgetInputLabel text={intl.formatMessage("widget.contact.input.enterContactLabel")} />
      <WidgetFormTextInput
        disabled={!phoneHandler}
        placeholder={
          phoneHandler
            ? intl.formatMessage("widget.contact.input.enterContactPlaceholder")
            : intl.formatMessage("initializing") + "..."
        }
        value={contact}
        onChange={(ev) => handleChange((ev.target as HTMLTextAreaElement).value)}
        onFocus={() => setHasError(false)}
        onBlur={handleBlur}
        errorHint={intl.formatMessage("widget.contact.input.enterContactError")}
        hasError={hasError}
      />
    </>
  );
};

const normalizeContact = (contact: string) => {
  return contact.replace(/\s/g, "");
};
